import {
  AppointmentStatusCodes,
  AppointmentType,
  DictionaryRecordDto,
  ResourceDto,
  SubtenantDto,
} from '@lmt/shared-types';
import { VisitType } from '@lmt/unicorn-shared/app/components/calendar/visit/common/types';
import { SettingsSeparatorLine } from '@lmt/unicorn-shared/app/components/common/ui-kit/configuration/SettingsSeparatorLine';
import {
  CalendarConfigurationFormComponentProps,
  ResourcesProps,
} from '@lmt/unicorn-shared/app/components/calendar/configuration/common/types';
import { Field } from '@lmt/unicorn-shared/app/components/common/Field';
import { useTags } from '@lmt/unicorn-shared/app/components/common/ui-kit/tags/useTags';
import { ToggleButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/ToggleButton';
import { MultiSelectDropDownInput } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/dropdown/MultiSelectDropDownInput';
import {
  EnumValue,
  getEnumValuesDict,
} from '@lmt/unicorn-shared/app/components/patients/list/common/types';
import { useActiveVisitTypes } from '@lmt/unicorn-shared/app/features/dictionaries/selectors';
import { useSubtenants } from '@lmt/unicorn-shared/app/features/subtenants/selectors';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import styles from './CalendarFilterSettings.module.scss';

export const CalendarFilterSettings: React.FC<
  CalendarConfigurationFormComponentProps & ResourcesProps
> = (props) => {
  const i18n = useTranslation();
  const i18nConfig = useScopedTranslation('calendar_configuration.filter');
  const { control, watch, register } = props.formMethods;
  const translatedVisitStatuses = useMemo(
    () => getEnumValuesDict(AppointmentStatusCodes, 'visit_status'),
    [],
  );
  const visitTypes = useActiveVisitTypes();
  const translatedVisitTypes: VisitType[] = useMemo(
    () =>
      visitTypes.map((type) => ({
        id: type.id,
        label: i18n.t(type.value as any),
      })),
    [visitTypes],
  );
  const tags = useTags();

  const subtenants: SubtenantDto[] = useSubtenants();

  const [
    isFilterDisplayed,
    hasFilterByPlaces,
    hasFilterBySpecialConditions,
    hasFilterBySubtenants,
    hasFilterByDoctors,
    hasFilterByVisitStatuses,
    hasFilterByTags,
    eventType,
  ] = watch([
    'isFilterDisplayed',
    'hasFilterByPlaces',
    'hasFilterBySpecialConditions',
    'hasFilterBySubtenants',
    'hasFilterByDoctors',
    'hasFilterByVisitStatuses',
    'hasFilterByTags',
    'shownEventType',
  ]);

  return (
    <>
      <div>
        <ToggleButton
          {...register('isFilterDisplayed')}
          title={i18nConfig.t('fields.filter')}
        />
      </div>
      {isFilterDisplayed && (
        <div>
          <SettingsSeparatorLine />
          <div className={styles.horizontalContainer}>
            <div className={styles.field}>
              <ToggleButton
                {...register('hasFilterByVisitTypes')}
                title={i18nConfig.t('fields.visit_types')}
              />
              {eventType !== AppointmentType.Meeting && (
                <Field title={''}>
                  <Controller
                    control={control}
                    name={'visitTypes'}
                    render={({ field: { onChange, value } }) => (
                      <div className={styles.input}>
                        <MultiSelectDropDownInput
                          options={translatedVisitTypes}
                          idFunction={'id'}
                          getOptionLabel={'label'}
                          placeholder={i18nConfig.t(
                            'fields.select_visit_types',
                          )}
                          allSelectedLabel={i18nConfig.t(
                            'fields.all_visit_types',
                          )}
                          onValueChanged={(places) => {
                            onChange(places.map((resource) => resource.id));
                          }}
                          values={value}
                          headerTitle={i18nConfig.t('fields.visit_types')}
                        />
                      </div>
                    )}
                  />
                </Field>
              )}
              <div className={styles.tipText}>
                <span className={styles.tipText}>
                  {i18nConfig.t('descriptions.event_types_tip')}
                </span>
              </div>
            </div>
          </div>
          <SettingsSeparatorLine />

          <div className={styles.horizontalContainer}>
            <div className={styles.dropdown}>
              <Field title={''}>
                <div className={styles.resourcesToggle}>
                  <ToggleButton
                    {...register('hasFilterByPlaces')}
                    title={i18nConfig.t('fields.places')}
                  />
                </div>
                {hasFilterByPlaces && (
                  <Controller
                    control={control}
                    name={'places'}
                    render={({ field: { onChange, value } }) => (
                      <MultiSelectDropDownInput<ResourceDto, 'id'>
                        options={props.places}
                        idFunction={'id'}
                        getOptionLabel={'name'}
                        placeholder={i18nConfig.t('fields.select_places')}
                        allSelectedLabel={i18nConfig.t('fields.all_places')}
                        onValueChanged={(places) => {
                          onChange(places.map((resource) => resource.id));
                        }}
                        values={value}
                        headerTitle={i18nConfig.t('fields.places')}
                      />
                    )}
                  />
                )}
              </Field>
            </div>
            <div className={styles.dropdown}>
              <Field title={''}>
                <div className={styles.resourcesToggle}>
                  <ToggleButton
                    {...register('hasFilterBySpecialConditions')}
                    title={i18nConfig.t('fields.special_conditions')}
                  />
                </div>
                {hasFilterBySpecialConditions && (
                  <Controller
                    control={control}
                    name={'specialConditions'}
                    render={({ field: { onChange, value } }) => (
                      <MultiSelectDropDownInput
                        options={props.specialConditions}
                        idFunction={'id'}
                        getOptionLabel={'name'}
                        placeholder={i18nConfig.t(
                          'fields.select_special_conditions',
                        )}
                        allSelectedLabel={i18nConfig.t(
                          'fields.all_special_conditions',
                        )}
                        onValueChanged={(specialConditions) => {
                          onChange(
                            specialConditions.map((resource) => resource.id),
                          );
                        }}
                        values={value}
                        headerTitle={i18nConfig.t('fields.special_conditions')}
                      />
                    )}
                  />
                )}
              </Field>
            </div>
            <div className={styles.dropdown}>
              <Field title={''}>
                <div className={styles.resourcesToggle}>
                  <ToggleButton
                    {...register('hasFilterByDoctors')}
                    title={i18nConfig.t('fields.doctors')}
                  />
                </div>
                {hasFilterByDoctors && (
                  <Controller
                    control={control}
                    name={'doctors'}
                    render={({ field: { onChange, value } }) => (
                      <MultiSelectDropDownInput
                        options={props.doctors}
                        idFunction={'id'}
                        getOptionLabel={'name'}
                        placeholder={i18n.t(
                          'calendar.filter.control.select_doctors',
                        )}
                        allSelectedLabel={i18n.t(
                          'calendar.filter.control.all_doctors',
                        )}
                        onValueChanged={(doctors) => {
                          onChange(doctors.map((doctor) => doctor.id));
                        }}
                        values={value}
                        headerTitle={i18nConfig.t('fields.doctors')}
                      />
                    )}
                  />
                )}
              </Field>
            </div>
          </div>
          <div className={styles.tipText}>
            <span className={styles.tipText}>
              {i18nConfig.t('descriptions.filter_resource_view')}
            </span>
          </div>
          <SettingsSeparatorLine />

          <div className={styles.horizontalContainer}>
            <div className={styles.dropdown}>
              <Field title={''} className={styles.dropdown}>
                <div className={styles.resourcesToggle}>
                  <ToggleButton
                    {...register('hasFilterBySubtenants')}
                    title={i18nConfig.t('fields.subtenants')}
                  />
                </div>
                {hasFilterBySubtenants && (
                  <Controller
                    control={control}
                    name={'subtenants'}
                    render={({ field: { onChange, value } }) => (
                      <MultiSelectDropDownInput
                        options={subtenants}
                        idFunction={'id'}
                        getOptionLabel={'name'}
                        placeholder={i18n.t(
                          'calendar.filter.control.select_subtenants',
                        )}
                        allSelectedLabel={i18n.t(
                          'calendar.filter.control.all_subtenants',
                        )}
                        onValueChanged={(subtenants) => {
                          onChange(subtenants.map((s) => s.id));
                        }}
                        values={value}
                        headerTitle={i18nConfig.t('fields.subtenants')}
                      />
                    )}
                  />
                )}
              </Field>
            </div>
            <div className={styles.dropdown}>
              <Field title={''} className={styles.dropdown}>
                <div className={styles.resourcesToggle}>
                  <ToggleButton
                    {...register('hasFilterByVisitStatuses')}
                    title={i18nConfig.t('fields.visit_statuses')}
                  />
                </div>
                {hasFilterByVisitStatuses && (
                  <Controller
                    control={control}
                    name={'visitStatuses'}
                    render={({ field: { onChange, value } }) => (
                      <div className={styles.input}>
                        <MultiSelectDropDownInput<EnumValue<number>, 'value'>
                          options={translatedVisitStatuses}
                          idFunction={'value'}
                          getOptionLabel={'label'}
                          placeholder={i18nConfig.t(
                            'fields.select_visit_statuses',
                          )}
                          allSelectedLabel={i18nConfig.t(
                            'fields.all_visit_statuses',
                          )}
                          onValueChanged={(newValues) => {
                            onChange(newValues.map((ev) => ev.value));
                          }}
                          values={value}
                          headerTitle={i18nConfig.t('fields.visit_statuses')}
                        />
                      </div>
                    )}
                  />
                )}
              </Field>
            </div>
            <div className={styles.dropdown}>
              <Field title={''} className={styles.dropdown}>
                <div className={styles.resourcesToggle}>
                  <ToggleButton
                    {...register('hasFilterByTags')}
                    title={i18nConfig.t('fields.tags')}
                  />
                </div>
                {hasFilterByTags && (
                  <Controller
                    control={control}
                    name={'tags'}
                    render={({ field: { onChange, value } }) => (
                      <div className={styles.input}>
                        <MultiSelectDropDownInput<DictionaryRecordDto, 'id'>
                          options={tags}
                          idFunction={'id'}
                          getOptionLabel={'value'}
                          placeholder={i18nConfig.t('fields.select_tags')}
                          allSelectedLabel={i18nConfig.t('fields.all_tags')}
                          onValueChanged={(newValues) => {
                            onChange(newValues.map((tag) => tag.id));
                          }}
                          values={value}
                          headerTitle={i18nConfig.t('fields.tags')}
                        />
                      </div>
                    )}
                  />
                )}
              </Field>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
